import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import {
  HiOutlinePencil,
  HiOutlineUserGroup,
  HiOutlineUserRemove,
} from 'react-icons/hi'

import {
  Box,
  Center,
  Flex,
  Grid,
  Icon,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import styled from '@emotion/styled'

import { ConfirmRemovalDialog, EditRemoveButton } from '@/components/ui'
import {
  ManagedAccountsDocument,
  TechniciansDocument,
  useDeleteTechnicianMutation,
  useDeleteUserMutation,
  useOffboardUserMutation,
} from '@/graphql/generated/hooks'
import { BUTTON_PRESS, mixpanel } from '@/utils/analytics'
import { getErrorMessage } from '@/utils/errorMessage'

import { RoleExtended } from '../types/types'
import { ChangeRolesModal } from './ChangeRolesModal'
import { EditTechnicianModal } from './EditTechnicianModal'
import { EditUserModal } from './EditUserModal'
import { RoleIcon, getRoleDisplayName } from './UserRoles'

const IconWrapperStyled = styled(Box)`
  .icon {
    width: 30px;
    margin-right: 10px;
  }
`

export const AccountEdit = ({
  role,
  userId,
  fullName,
}: {
  role: RoleExtended
  userId: string
  fullName: string
}) => {
  const {
    isOpen: isEditAccountOpen,
    onOpen: onEditAccountOpen,
    onClose: onEditAccountClose,
  } = useDisclosure()
  const {
    isOpen: isRemoveAccountOpen,
    onOpen: onRemoveAccountOpen,
    onClose: onRemoveAccountClose,
  } = useDisclosure()
  const {
    isOpen: isOffboardOpen,
    onOpen: onOffboardOpen,
    onClose: onOffboardClose,
  } = useDisclosure()
  const {
    isOpen: isChangeRolesOpen,
    onOpen: onChangeRolesOpen,
    onClose: onChangeRolesClose,
  } = useDisclosure()

  const mode = useLightMode()
  const { showSuccess, showError } = useToasts()
  const { hasCRUDPermission } = usePermissions()
  const isUserManagementV1Enabled = useFeatureFlag(FeatureFlag.userManagementV1)
  const hasPermission = hasCRUDPermission(role)
  const [deleteUser, { loading: isDeleteUserLoading }] = useDeleteUserMutation()
  const [deleteTechnician, { loading: isDeleteTechnicianLoading }] =
    useDeleteTechnicianMutation()
  const [offboardUser, { loading: isOffboardLoading }] =
    useOffboardUserMutation()
  const isDeleteAccountLoading =
    role === RoleExtended.TECHNICIAN
      ? isDeleteTechnicianLoading
      : isDeleteUserLoading

  const onAccountRemove = async () => {
    try {
      if (role === RoleExtended.TECHNICIAN) {
        await deleteTechnician({
          variables: {
            input: {
              id: userId,
            },
          },
          refetchQueries: [
            {
              query: TechniciansDocument,
            },
          ],
        })
      } else {
        await deleteUser({
          variables: {
            input: {
              id: userId,
            },
          },
          refetchQueries: [
            {
              query: ManagedAccountsDocument,
            },
          ],
        })
      }
      showSuccess(`${getRoleDisplayName(role)} removed.`)
      onRemoveAccountClose()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const onAccountOffboard = async () => {
    mixpanel.track(`${BUTTON_PRESS} Offboard User Submit`, { userId, role })
    try {
      await offboardUser({
        variables: {
          input: {
            id: userId,
          },
        },
        refetchQueries: [
          {
            query: ManagedAccountsDocument,
          },
        ],
      })
      mixpanel.track('Offboard User Success', { userId, role })
      showSuccess(`${fullName} has been offboarded.`)
      onOffboardClose()
    } catch (error) {
      mixpanel.track('Offboard User Failure', {
        userId,
        role,
        error: getErrorMessage(error),
      })
      showError(getErrorMessage(error))
    }
  }

  const isTechnician = role === RoleExtended.TECHNICIAN
  const shouldShowOffboard = isUserManagementV1Enabled && !isTechnician
  const shouldShowChangeRoles = isUserManagementV1Enabled && !isTechnician

  return (
    <Box height={shouldShowChangeRoles ? 130 : 90}>
      {hasPermission &&
        (shouldShowOffboard ? (
          <Grid
            gap='10px'
            gridTemplateRows='1fr 1fr 1fr'
            h='full'
            maxW='100%'
            ml='10px'
          >
            <Tooltip hasArrow label='Change Roles' placement='left'>
              <Center
                _hover={{ bgColor: `btn.change-roles-hover.${mode}` }}
                bgColor={`btn.change-roles.${mode}`}
                boxShadow={`shadow.card.${mode}`}
                cursor='pointer'
                data-testid='EditRemoveButton:changeRoles'
                h='full'
                onClick={() => {
                  mixpanel.track(`${BUTTON_PRESS} Change Roles Open`, {
                    userId,
                    role,
                  })
                  onChangeRolesOpen()
                }}
                rounded='5px'
                transition='ease-in-out'
                transitionDuration='300ms'
                transitionProperty='background-color'
                w='60px'
              >
                <Icon
                  as={HiOutlineUserGroup}
                  boxSize='24px'
                  color={`icon.primary.${mode}`}
                />
              </Center>
            </Tooltip>
            <Tooltip hasArrow label='Edit User' placement='left'>
              <Center
                _hover={{ bgColor: `btn.edit-hover.${mode}` }}
                bgColor={`btn.edit.${mode}`}
                boxShadow={`shadow.card.${mode}`}
                cursor='pointer'
                data-testid='EditRemoveButton:edit'
                h='full'
                onClick={onEditAccountOpen}
                rounded='5px'
                transition='ease-in-out'
                transitionDuration='300ms'
                transitionProperty='background-color'
                w='60px'
              >
                <Icon as={HiOutlinePencil} boxSize='24px' color='brand.900' />
              </Center>
            </Tooltip>
            <Tooltip hasArrow label='Offboard User' placement='left'>
              <Center
                _hover={{ bgColor: `btn.offboard-hover.${mode}` }}
                bgColor={`btn.offboard.${mode}`}
                boxShadow={`shadow.card.${mode}`}
                cursor='pointer'
                data-testid='EditRemoveButton:offboard'
                h='full'
                onClick={() => {
                  mixpanel.track(`${BUTTON_PRESS} Offboard User Open`, {
                    userId,
                    role,
                  })
                  onOffboardOpen()
                }}
                rounded='5px'
                transition='ease-in-out'
                transitionDuration='300ms'
                transitionProperty='background-color'
                w='60px'
              >
                <Icon
                  as={HiOutlineUserRemove}
                  boxSize='24px'
                  color={`icon.primary.${mode}`}
                />
              </Center>
            </Tooltip>
          </Grid>
        ) : (
          <EditRemoveButton
            handleOnDelete={onRemoveAccountOpen}
            handleOnEdit={onEditAccountOpen}
          />
        ))}
      {isEditAccountOpen &&
        (role !== RoleExtended.TECHNICIAN ? (
          <EditUserModal
            isOpen={isEditAccountOpen}
            onClose={onEditAccountClose}
            onOffboardOpen={shouldShowOffboard ? onOffboardOpen : undefined}
            onRemoveOpen={onRemoveAccountOpen}
            role={role}
            userId={userId}
          />
        ) : (
          <EditTechnicianModal
            isOpen={isEditAccountOpen}
            onClose={onEditAccountClose}
            onRemoveOpen={onRemoveAccountOpen}
            technicianId={userId}
          />
        ))}
      {isRemoveAccountOpen && (
        <ConfirmRemovalDialog
          handleOnClick={onAccountRemove}
          isLoading={isDeleteAccountLoading}
          isOpen={isRemoveAccountOpen}
          itemTitle={`${getRoleDisplayName(role)} ${fullName}`}
          onClose={onRemoveAccountClose}
          title={
            <Flex alignItems='center'>
              <IconWrapperStyled>
                <RoleIcon role={role} />
              </IconWrapperStyled>
              <Box as='span'>Remove {getRoleDisplayName(role)}</Box>
            </Flex>
          }
        />
      )}
      {isOffboardOpen && (
        <ConfirmRemovalDialog
          actionButtonText='Offboard User'
          bodyText='Are you sure you want to offboard '
          descriptionText='This will immediately log out the user, remove all their roles, and disable their account.'
          handleOnClick={onAccountOffboard}
          hideCanBeUndone
          isLoading={isOffboardLoading}
          isOpen={isOffboardOpen}
          itemTitle={fullName}
          onClose={onOffboardClose}
          title={
            <Flex alignItems='center'>
              <HiOutlineUserRemove
                size='24px'
                style={{ marginRight: '10px' }}
              />
              <Box as='span'>Offboard User</Box>
            </Flex>
          }
        />
      )}
      {isChangeRolesOpen && (
        <ChangeRolesModal
          fullName={fullName}
          isOpen={isChangeRolesOpen}
          onClose={onChangeRolesClose}
          userId={userId}
        />
      )}
    </Box>
  )
}
